import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw } from 'lucide-react';

/**
 * Branded error page used by both `<ErrorBoundary>` (page level) and
 * `Sentry.ErrorBoundary` (root). Provides a Reload button and a Report
 * mailto so users can flag persistent issues even with no JS errors logged.
 *
 * @param {object} props
 * @param {Error}  [props.error]                   Caught error (forwarded by Sentry).
 * @param {() => void} [props.resetError]          Sentry-supplied reset.
 *
 * @example
 *   <Sentry.ErrorBoundary fallback={(p) => <ErrorFallback {...p} />} />
 */
export default function ErrorFallback({ error, resetError }) {
  const { t } = useTranslation('errors');

  const handleReload = () => {
    if (resetError) resetError();
    if (typeof window !== 'undefined') window.location.reload();
  };

  const subject = encodeURIComponent('[Inside Panama] Site error');
  const body = encodeURIComponent(
    `Page: ${typeof window !== 'undefined' ? window.location.href : ''}\n\n` +
      (error ? `Error: ${error.message}\n\n${error.stack ?? ''}` : ''),
  );
  const mailto = `mailto:hello@insidepanama.com?subject=${subject}&body=${body}`;

  return (
    <div
      role="alert"
      className="flex min-h-screen items-center justify-center bg-cream-50 px-4 py-16"
    >
      <div className="max-w-md text-center">
        <p className="v4-eyebrow mb-4">{t('generic')}</p>
        <h1 className="mb-3">{t('boundaryTitle')}</h1>
        <p className="text-gray-700">{t('boundaryBody')}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={handleReload} className="btn-primary">
            <RefreshCw size={16} aria-hidden="true" />
            {t('reload')}
          </button>
          <a href={mailto} className="btn-secondary">
            <Mail size={16} aria-hidden="true" />
            Report
          </a>
        </div>
      </div>
    </div>
  );
}
